import { useEffect, useMemo, useState } from 'react';
import type { Resultados, ResumenResultados } from '../../api/ops/campanas';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoVacia } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { estadoDeError } from '../../logica/estados';
import { cifra, importe } from '../../logica/fechasCortas';
import type { PropsPantalla } from '../tipos';
import { Chip } from '../../componentes/Chip';
import { periodoAnterior, periodos, porcentaje, variacion, type Periodo } from './reglas';

/**
 * Resumen del periodo (12.4, `getCampaignResultsSummary`): cifras de campañas y mensajes automáticos juntos, barras
 * «Reservas y compras por campaña» con el nombre de cada una (`name`, G802) y la que más trajo. Lo ven Propietario y
 * Administrador desde «Ver resultados» y el Socio como su única pantalla (G459, G776), siempre sin datos de ningún cliente.
 * Cada cifra lleva su chip frente al periodo anterior de la misma duración (`compare=previous_period`, `previous_totals`,
 * G828); la cabecera escribe ese periodo («frente a los 30 días justo antes (15 ago – 13 sep)», G892). En las bajas,
 * subir es malo.
 */
interface Props extends Pick<PropsPantalla, 'ops' | 'negocio' | 'movil'> {
    socio: boolean;
    /** Abrir una campaña o ir a los automáticos al tocar una barra (no el Socio). */
    onAbrir?: (fila: Resultados) => void;
}

export function Resumen({ ops, negocio, movil, socio, onAbrir }: Props) {
    const lista = useMemo(() => periodos(), []);
    const [valor, setValor] = useState<Periodo['valor']>('30d');
    const periodo = lista.find((p) => p.valor === valor) ?? lista[0];
    const [datos, setDatos] = useState<ResumenResultados | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);

    useEffect(() => {
        if (periodo === undefined) {
            return;
        }
        let vivo = true;
        ops.getCampaignResultsSummary(negocio, { from: periodo.from, to: periodo.to, compare: 'previous_period' })
            .then((r) => {
                if (vivo) {
                    setDatos(r);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, periodo, intento]);

    if (periodo === undefined) {
        return null;
    }
    const antes = periodoAnterior(periodo.from, periodo.to);
    const cab = (
        <div className="rv-cp-resumen__cab">
            <p>{`${socio ? 'Campañas y mensajes automáticos' : 'Campañas y mensajes automáticos juntos'} · ${periodo.etiqueta.toLocaleLowerCase('es')}${antes === null ? '' : ` frente a ${antes}`}`}</p>
            <Desplegable etiqueta="Periodo" valor={valor} opciones={lista.map((p) => ({ valor: p.valor, texto: p.etiqueta }))} onChange={(v) => { setDatos(null); setValor(v as Periodo['valor']); }} />
        </div>
    );
    if (error !== null) {
        return (
            <section className="rv-cp-resumen" aria-label="Resultados del periodo">
                {cab}
                <EstadoError seccion="los resultados" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setError(null); setDatos(null); setIntento((i) => i + 1); }} movil={movil} />
            </section>
        );
    }
    if (datos === null) {
        return (
            <section className="rv-cp-resumen" aria-label="Resultados del periodo">
                {cab}
                <EstadoCargando seccion="los resultados" />
            </section>
        );
    }
    const t = datos.totals;
    const p = datos.previous_totals ?? null;
    const chip = (actual: number | null | undefined, anterior: number | null | undefined, menos = false) => variacion(actual, anterior, menos) ?? undefined;
    const filas = [...datos.campaigns, ...datos.automations].filter((f) => f.conversions > 0 || f.sent > 0).sort((a, b) => b.conversions - a.conversions);
    const totalConv = filas.reduce((a, f) => a + f.conversions, 0);
    const mejor = filas[0];
    const nombreDe = (f: Resultados) => f.name ?? (f.subject_kind === 'automation' ? 'Mensaje automático' : 'Campaña');
    const vacio = t.sent === 0 && t.conversions === 0 && filas.length === 0;

    return (
        <section className="rv-cp-resumen" aria-label="Resultados del periodo">
            {cab}
            {vacio ? (
                <EstadoVacia seccion="los resultados" />
            ) : (
                <>
                    <div className="rv-cp-cifras">
                        <Cifra titulo="Reservas y compras" valor={cifra(t.conversions)} pie={t.conversions_amount !== undefined && t.conversions_amount.amount > 0 ? `${importe(t.conversions_amount)} en los 7 días siguientes` : 'En los 7 días siguientes'} oscura cambio={chip(t.conversions, p?.conversions)} />
                        <Cifra titulo="Enviados" valor={cifra(t.sent)} pie={`${String(datos.campaigns.length)} ${datos.campaigns.length === 1 ? 'campaña' : 'campañas'} y los automáticos`} cambio={chip(t.sent, p?.sent)} />
                        <Cifra titulo="Entregados" valor={t.delivered === null ? '—' : cifra(t.delivered)} pie={t.sent > 0 && t.delivered !== null ? `${porcentaje(t.delivered, t.sent)} de los enviados` : 'Todavía nada'} cambio={chip(t.delivered, p?.delivered)} />
                        <Cifra titulo="Leídos" valor={t.read == null ? '—' : cifra(t.read)} pie="Solo WhatsApp: el email no se mide" cambio={chip(t.read, p?.read)} />
                        <Cifra titulo="Bajas" valor={cifra(t.unsubscribes)} pie="Solo el número, nunca quién" cambio={chip(t.unsubscribes, p?.unsubscribes, true)} />
                    </div>
                    {filas.length > 0 && (
                        <div className="rv-cp-barras">
                            <h3>Reservas y compras por campaña</h3>
                            <p>En los 7 días siguientes a cada envío</p>
                            {filas.map((f) => {
                                const ancho = totalConv === 0 ? 0 : Math.round((f.conversions / totalConv) * 100);
                                const contenido = (
                                    <>
                                        <span>
                                            <Icono nombre={f.subject_kind === 'automation' ? 'zap' : 'send'} /> {nombreDe(f)}
                                        </span>
                                        <span className="rv-cp-pista-barra" aria-hidden="true">
                                            <span style={{ width: `${String(ancho)}%` }} />
                                        </span>
                                        <strong>{cifra(f.conversions)}</strong>
                                    </>
                                );
                                return onAbrir === undefined || socio ? (
                                    <div key={f.subject_id} className="rv-cp-barra-fila">
                                        {contenido}
                                    </div>
                                ) : (
                                    <button key={f.subject_id} type="button" className="rv-cp-barra-fila" aria-label={`${nombreDe(f)}: ${cifra(f.conversions)} reservas y compras. Ver sus resultados`} onClick={() => { onAbrir(f); }}>
                                        {contenido}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {mejor !== undefined && mejor.conversions > 0 && (
                        <div className="rv-cp-mejor">
                            <strong>La que más trajo</strong>
                            <span>
                                {`${nombreDe(mejor)} · ${cifra(mejor.conversions)} reservas y compras · ${porcentaje(mejor.conversions, totalConv)} de todas`}
                                {mejor.conversions_amount !== undefined && mejor.conversions_amount.amount > 0 ? ` · ${importe(mejor.conversions_amount)}` : ''}
                                {mejor.delivered !== null ? ` · ${cifra(mejor.delivered)} entregados` : ''}
                            </span>
                        </div>
                    )}
                </>
            )}
        </section>
    );
}

const ESTADO_CAMBIO = { bueno: 'confirmed', malo: 'problem', neutro: 'neutral' } as const;

export function Cifra({ titulo, valor, pie, oscura = false, cambio }: { titulo: string; valor: string; pie?: string; oscura?: boolean; cambio?: { texto: string; tono: 'bueno' | 'malo' | 'neutro' } }) {
    return (
        <div className={`rv-cp-cifra${oscura ? ' rv-cp-cifra--oscura' : ''}`}>
            <span>{titulo}</span>
            <strong>{valor}</strong>
            {cambio !== undefined && (
                <span className="rv-cp-cifra__cambio" title="Frente al periodo anterior">
                    <Chip estado={ESTADO_CAMBIO[cambio.tono]}>{cambio.texto}</Chip>
                </span>
            )}
            {pie !== undefined && <small>{pie}</small>}
        </div>
    );
}
